/**
 * Family Column Header Component
 *
 * Renders column headers for family member columns.
 * Displays avatar circles with colored borders and names below.
 */

import { SimpleAvatar } from '@/components/shared/SimpleAvatar';
import { ModernPalette } from '@/constants/theme';
import type { FamilyMemberResource } from '@/types/family-resource';
import React from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';

const AVATAR_SIZE = 32;
const BORDER_WIDTH = 2;

type FamilyColumnHeaderProps = {
  visibleResources: FamilyMemberResource[];
  columnWidth: number;
  textColor: string;
  onAvatarPress?: (resource: FamilyMemberResource) => void;
};

export const FamilyColumnHeader = React.memo(function FamilyColumnHeader({
  visibleResources,
  columnWidth,
  textColor,
  onAvatarPress,
}: FamilyColumnHeaderProps) {
  return (
    <>
      {visibleResources.map((resource) => (
        <View
          key={resource.id}
          style={[styles.resourceHeaderContainer, { width: columnWidth }]}
        >
          <Pressable
            style={styles.resourceHeaderContent}
            onPress={onAvatarPress ? () => onAvatarPress(resource) : undefined}
          >
            <View
              style={[
                styles.avatarBorder,
                { borderColor: resource.color || ModernPalette.eventBlue },
              ]}
            >
              <SimpleAvatar
                name={resource.name}
                color={resource.color || ModernPalette.eventBlue}
                size={AVATAR_SIZE - BORDER_WIDTH * 2}
                imageUri={resource.profilePicture}
              />
            </View>
            <Text
              style={[styles.resourceHeaderText, { color: textColor }]}
              numberOfLines={1}
            >
              {resource.name}
            </Text>
          </Pressable>
        </View>
      ))}
    </>
  );
});

const styles = StyleSheet.create({
  resourceHeaderContainer: {
    height: 60,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 4,
  },
  resourceHeaderContent: {
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
  },
  avatarBorder: {
    width: AVATAR_SIZE,
    height: AVATAR_SIZE,
    borderRadius: AVATAR_SIZE / 2,
    borderWidth: BORDER_WIDTH,
    alignItems: 'center',
    justifyContent: 'center',
  },
  resourceHeaderText: {
    fontSize: 10,
    fontWeight: '600',
    textAlign: 'center',
  },
});
